/**
 * Green Button XML (NAESB ESPI) reader.
 *
 * Green Button is the one interval format every utility that offers a customer
 * download shares: an Atom feed whose entries carry UsagePoints, ReadingTypes,
 * MeterReadings and IntervalBlocks of IntervalReadings. This module turns it
 * into the same two-column sheet every other upload becomes, a timestamp and a
 * kilowatt reading per row, so the rest of the parser (classification,
 * profiling, invariants, plausibility, disclosures, audit) runs on it unchanged.
 * Nothing downstream knows the file was XML.
 *
 * What the reader decides, and only this:
 *   - Which readings count. Electricity usage points only (ServiceCategory
 *     kind 0; a gas point in the same feed is ignored). Delivered energy only:
 *     a reverse-flow ReadingType (flowDirection 19, the export channel of a
 *     net-metered home) is noted, never summed. More than one electric usage
 *     point with delivered readings is refused, the way an interleaved
 *     multi-meter CSV is.
 *   - The unit. Watt-hours (uom 72) become kW through each reading's own
 *     duration; watts (uom 38) become kW directly; VA and VAh become kVA the
 *     same two ways. powerOfTenMultiplier is applied first. Anything else is
 *     refused by name.
 *   - The instant. ESPI timestamps are epoch seconds, so they are true
 *     instants and are written as UTC. There is no wall-clock ambiguity and no
 *     daylight-saving repeat in this format.
 *
 * The XML is scanned with regular expressions rather than a DOM: ESPI files
 * are machine-written and regular, the browser's DOMParser is absent under
 * Node (where the corpus runs), and a 10 MB feed must not be built as a tree.
 * Namespace prefixes (espi:, none, or anything else) are tolerated everywhere.
 */

import { GREEN_BUTTON_ESTIMATED_QUALITY } from './estimatedReadings';

export interface GreenButtonRead {
  /** Header row plus one row per delivered reading: [timestamp text, kW text]. */
  rows: (string | null)[][];
  /** The unit the value column carries, which also names the header. */
  unit: 'kW' | 'kVA';
  readingCount: number;
  /** A reverse-flow (export) channel was present and left out of the demand. */
  reverseFlowPresent: boolean;
  /** Distinct interval lengths seen, in seconds. */
  intervalSeconds: number[];
  /** Electric usage points found. */
  usagePoints: number;
  /** A refusal, with the reason a person can act on. */
  error: string | null;
  /** ISO instants of readings whose ReadingQuality says estimated (estimatedReadings.ts). */
  estimatedTimestamps: string[];
}

export const GREEN_BUTTON_TIMESTAMP_HEADER = 'IntervalReading start (UTC)';

function valueHeader(unit: 'kW' | 'kVA'): string {
  return `IntervalReading value (${unit})`;
}

/** Cheap sniff: an Atom feed that mentions ESPI interval readings. */
export function isGreenButtonXml(text: string): boolean {
  const head = text.slice(0, 4000);
  return /<(?:[\w.-]+:)?feed\b/.test(head) && /naesb\.org\/espi|IntervalBlock|IntervalReading/.test(text);
}

const UOM_NAMES: Record<number, string> = {
  5: 'amperes', 29: 'volts', 31: 'joules', 33: 'hertz', 38: 'watts', 42: 'cubic meters',
  61: 'volt-amperes', 63: 'volt-amperes reactive', 71: 'volt-ampere hours', 72: 'watt-hours',
  73: 'volt-ampere reactive hours', 119: 'cubic feet', 139: 'BTU', 169: 'therms',
};

function tagText(xml: string, name: string): string | null {
  const m = xml.match(new RegExp(`<(?:[\\w.-]+:)?${name}\\b[^>]*>([\\s\\S]*?)</(?:[\\w.-]+:)?${name}>`));
  return m ? m[1].trim() : null;
}

function tagInt(xml: string, name: string): number | null {
  const t = tagText(xml, name);
  if (t === null || t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function hasTag(xml: string, name: string): boolean {
  return new RegExp(`<(?:[\\w.-]+:)?${name}\\b`).test(xml);
}

function links(entryXml: string, rel: string): string[] {
  const out: string[] = [];
  const re = /<(?:[\w.-]+:)?link\b([^>]*)\/?>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(entryXml)) !== null) {
    const attrs = m[1];
    const relMatch = attrs.match(/\brel\s*=\s*"([^"]*)"/);
    const hrefMatch = attrs.match(/\bhref\s*=\s*"([^"]*)"/);
    if (relMatch && hrefMatch && relMatch[1] === rel) out.push(normalizeHref(hrefMatch[1]));
  }
  return out;
}

/** Hrefs in one feed can be absolute or relative and may carry a query; compare on the path. */
function normalizeHref(href: string): string {
  return href.replace(/^https?:\/\/[^/]+/i, '').replace(/[?#].*$/, '').replace(/\/+$/, '');
}

interface ReadingType {
  flowDirection: number | null;
  uom: number | null;
  multiplier: number;
  intervalLength: number | null;
}

function toKilo(value: number, rt: ReadingType, durationSeconds: number | null): { kilo: number; unit: 'kW' | 'kVA' } | { refusal: string } {
  const scaled = value * Math.pow(10, rt.multiplier);
  const hours = durationSeconds && durationSeconds > 0 ? durationSeconds / 3600 : null;
  switch (rt.uom) {
    case 38:
      return { kilo: scaled / 1000, unit: 'kW' };
    case 61:
      return { kilo: scaled / 1000, unit: 'kVA' };
    case 72:
      if (hours === null) return { refusal: 'a watt-hour reading carries no interval duration, so it cannot be converted to kW' };
      return { kilo: scaled / 1000 / hours, unit: 'kW' };
    case 71:
      if (hours === null) return { refusal: 'a volt-ampere-hour reading carries no interval duration, so it cannot be converted to kVA' };
      return { kilo: scaled / 1000 / hours, unit: 'kVA' };
    default: {
      const name = rt.uom === null ? 'no unit' : (UOM_NAMES[rt.uom] ?? `unit code ${rt.uom}`);
      return { refusal: `this Green Button file reports ${name} rather than energy or power. TrueLoad needs watt-hour or watt readings` };
    }
  }
}

function fmtUtc(epochSeconds: number): string {
  // "YYYY-MM-DD HH:MM:SSZ": the parser's explicit-offset form, read as the exact instant.
  return new Date(epochSeconds * 1000).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, 'Z');
}

export function readGreenButton(xml: string): GreenButtonRead {
  const empty = (error: string): GreenButtonRead => ({
    rows: [], unit: 'kW', readingCount: 0, reverseFlowPresent: false, intervalSeconds: [], usagePoints: 0, error,
    estimatedTimestamps: [],
  });

  const entries: string[] = [];
  const entryRe = /<(?:[\w.-]+:)?entry\b[^>]*>([\s\S]*?)<\/(?:[\w.-]+:)?entry>/g;
  let m: RegExpExecArray | null;
  while ((m = entryRe.exec(xml)) !== null) entries.push(m[1]);
  if (entries.length === 0) {
    return empty('This XML file has no Green Button entries. It may be a different kind of XML export.');
  }

  // Pass 1: the reference data. Usage points by href, reading types by href,
  // meter readings by href with the reading types they point at. Each block
  // also remembers the ReadingType entry just before it: Southern California
  // Edison links one MeterReading to a delivered and a received ReadingType and
  // says which block is which only by feed order (ReadingType, its blocks,
  // ReadingType, its blocks), with the same block href in both.
  const usagePointKinds = new Map<string, number | null>();
  const readingTypes = new Map<string, ReadingType>();
  const meterReadingTypeHrefs = new Map<string, string[]>();
  const blocks: Array<{ selfHref: string; upHref: string | null; xml: string; precedingReadingType: string | null }> = [];
  let lastReadingTypeHref: string | null = null;

  for (const e of entries) {
    const self = links(e, 'self')[0] ?? '';
    if (hasTag(e, 'UsagePoint')) {
      const kind = tagInt(tagText(e, 'ServiceCategory') ?? '', 'kind');
      usagePointKinds.set(self, kind);
    } else if (hasTag(e, 'ReadingType')) {
      readingTypes.set(self, {
        flowDirection: tagInt(e, 'flowDirection'),
        uom: tagInt(e, 'uom'),
        multiplier: tagInt(e, 'powerOfTenMultiplier') ?? 0,
        intervalLength: tagInt(e, 'intervalLength'),
      });
      lastReadingTypeHref = self;
    } else if (hasTag(e, 'MeterReading')) {
      meterReadingTypeHrefs.set(self, links(e, 'related').filter(h => /ReadingType/i.test(h)));
    } else if (hasTag(e, 'IntervalBlock')) {
      blocks.push({ selfHref: self, upHref: links(e, 'up')[0] ?? null, xml: e, precedingReadingType: lastReadingTypeHref });
    }
  }

  if (blocks.length === 0) {
    return empty('This Green Button file carries no interval readings. Export the interval (15-minute or hourly) usage data, not the bill summary.');
  }

  // A block belongs to the meter reading its "up" link (or its own href) names,
  // and that meter reading belongs to the usage point in its path.
  const meterReadingOf = (b: { selfHref: string; upHref: string | null }): string | null => {
    const source = b.upHref ?? b.selfHref;
    const mm = source.match(/^(.*\/MeterReading\/[^/]+)/i);
    return mm ? mm[1] : null;
  };
  const usagePointOf = (href: string | null): string | null => {
    const mm = href?.match(/^(.*\/UsagePoint\/[^/]+)/i);
    return mm ? mm[1] : null;
  };
  const onlyReadingType = readingTypes.size === 1 ? [...readingTypes.values()][0] : null;

  // A meter reading with several reading types: feed order has to account for
  // every one of them, or the file has not said which readings are delivered.
  // Guessing here could report exported solar as the home's peak, so refuse.
  for (const [mrHref, hrefs] of meterReadingTypeHrefs) {
    if (hrefs.length < 2) continue;
    const own = blocks.filter(b => meterReadingOf(b) === mrHref);
    const claimed = new Set(own.map(b => b.precedingReadingType));
    if (own.length > 0 && (claimed.size !== hrefs.length || !hrefs.every(h => claimed.has(h)))) {
      return empty('This Green Button file does not say which readings are energy delivered to the home and which are sent back to the grid. Ask the utility for the standard Green Button export.');
    }
  }

  const electricPoints = new Set<string>();
  let reverseFlowPresent = false;
  let unit: 'kW' | 'kVA' | null = null;
  const intervalSet = new Set<number>();
  const readings: Array<{ start: number; kilo: number; estimated: boolean }> = [];

  for (const b of blocks) {
    const mrHref = meterReadingOf(b);
    const upHref = usagePointOf(mrHref ?? b.selfHref);
    if (upHref && usagePointKinds.has(upHref)) {
      const kind = usagePointKinds.get(upHref);
      // kind 0 is electricity; a feed can also carry gas (1) or water (2).
      if (kind !== null && kind !== undefined && kind !== 0) continue;
    }
    const rtHrefs = mrHref ? meterReadingTypeHrefs.get(mrHref) ?? [] : [];
    const rtHref = rtHrefs.length > 1 ? b.precedingReadingType : rtHrefs[0] ?? null;
    const rt = (rtHref ? readingTypes.get(rtHref) : null) ?? onlyReadingType;
    if (!rt) {
      return empty('This Green Button file describes readings without saying what they measure (no ReadingType). Ask the utility for the standard Green Button export.');
    }
    if (rt.flowDirection === 19) {
      reverseFlowPresent = true;
      continue;
    }
    if (upHref) electricPoints.add(upHref);

    const blockDefault = tagInt(tagText(b.xml, 'interval') ?? '', 'duration');
    const readingRe = /<(?:[\w.-]+:)?IntervalReading\b[^>]*>([\s\S]*?)<\/(?:[\w.-]+:)?IntervalReading>/g;
    let r: RegExpExecArray | null;
    while ((r = readingRe.exec(b.xml)) !== null) {
      const body = r[1];
      const period = tagText(body, 'timePeriod') ?? '';
      const start = tagInt(period, 'start');
      const duration = tagInt(period, 'duration') ?? rt.intervalLength ?? blockDefault;
      const value = tagInt(body, 'value');
      if (start === null || value === null) continue;
      const converted = toKilo(value, rt, duration);
      if ('refusal' in converted) {
        return empty(`This Green Button file could not be read: ${converted.refusal}.`);
      }
      if (unit === null) unit = converted.unit;
      else if (unit !== converted.unit) {
        return empty('This Green Button file mixes power and apparent-power readings in one channel, which TrueLoad does not combine.');
      }
      if (duration && duration > 0) intervalSet.add(duration);
      const quality = tagInt(tagText(body, 'ReadingQuality') ?? '', 'quality');
      readings.push({
        start,
        kilo: Math.round(converted.kilo * 1e6) / 1e6,
        estimated: quality !== null && GREEN_BUTTON_ESTIMATED_QUALITY.has(quality),
      });
    }
  }

  if (electricPoints.size > 1) {
    return empty(
      `This Green Button file holds ${electricPoints.size} electric meters. TrueLoad reads one meter at a time: ` +
      'export the meter for this service alone, or ask your contractor which one to use.'
    );
  }
  if (readings.length === 0) {
    return empty(
      reverseFlowPresent
        ? 'This Green Button file only carries the export (generation) channel; the delivered-energy channel is missing.'
        : 'This Green Button file carries no readable interval readings.'
    );
  }

  readings.sort((a, b) => a.start - b.start);
  const finalUnit: 'kW' | 'kVA' = unit ?? 'kW';
  const rows: (string | null)[][] = [[GREEN_BUTTON_TIMESTAMP_HEADER, valueHeader(finalUnit)]];
  for (const rd of readings) rows.push([fmtUtc(rd.start), String(rd.kilo)]);

  return {
    rows,
    unit: finalUnit,
    readingCount: readings.length,
    reverseFlowPresent,
    intervalSeconds: [...intervalSet].sort((a, b) => a - b),
    usagePoints: Math.max(electricPoints.size, 1),
    error: null,
    estimatedTimestamps: readings.filter(rd => rd.estimated).map(rd => new Date(rd.start * 1000).toISOString()),
  };
}
